/**
 * Mixpanel telemetry — Sprint 9 pilot launch.
 *
 * Önemli kurallar:
 *   - Yalnızca KVKK açık rıza ile (`consents.attOptIn === true`).
 *   - Hiçbir PII gönderilmez (e-posta, ad, kart numarası, fiş içeriği).
 *   - Şirket + kullanıcı ID hash'lenmiş şekilde — re-identification engellensin.
 *   - Mixpanel token yoksa no-op (lokal dev'de gürültü olmaz).
 */

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;

let mixpanelLoaded = false;
let optInConsent = false;

interface MixpanelLib {
  init(token: string, opts?: Record<string, unknown>): void;
  track(name: string, props?: Record<string, unknown>): void;
  identify(id: string): void;
  register(props: Record<string, unknown>): void;
  reset(): void;
  opt_out_tracking(): void;
  opt_in_tracking(): void;
}

let mixpanel: MixpanelLib | null = null;

async function ensureLoaded(): Promise<MixpanelLib | null> {
  if (!MIXPANEL_TOKEN) return null;
  if (mixpanelLoaded && mixpanel) return mixpanel;

  try {
    const mod = await import('mixpanel-browser');
    mixpanel = mod.default as unknown as MixpanelLib;
    mixpanel.init(MIXPANEL_TOKEN, {
      api_host: 'https://api-eu.mixpanel.com',
      track_pageview: false,
      persistence: 'localStorage',
      ip: false,
      property_blacklist: ['$current_url', '$initial_referrer'],
    });
    mixpanelLoaded = true;
    return mixpanel;
  } catch {
    return null;
  }
}

/**
 * KVKK consent flag — login sonrası `/kvkk/consents`'ten okunur.
 */
export function setAnalyticsConsent(optIn: boolean): void {
  optInConsent = optIn;
  if (!optIn && mixpanel) {
    mixpanel.opt_out_tracking();
  } else if (optIn && mixpanel) {
    mixpanel.opt_in_tracking();
  }
}

/**
 * Login sonrası — pseudonymized id (Cognito sub'tan türeyen hash).
 * Şirket bazında segmentasyon için super property olarak set edilir.
 */
export async function identify(args: { userIdHash: string; companyIdHash: string; role: string }): Promise<void> {
  if (!optInConsent) return;
  const m = await ensureLoaded();
  if (!m) return;
  m.identify(args.userIdHash);
  m.register({
    company_id_hash: args.companyIdHash,
    role: args.role,
    app: 'web-admin',
  });
}

/**
 * Pilot charter funnel:
 *   pilot.feature_used → kayıt, kart, fiş, OCR, e-fatura, export
 *   pilot.error_encountered → modül + error_code (PII'siz)
 *   pilot.action_completed → onboarding, ilk-tx, ilk-fiş, mapping-set
 */
export async function track(event: string, props?: Record<string, unknown>): Promise<void> {
  if (!optInConsent) return;
  const m = await ensureLoaded();
  if (!m) return;
  m.track(event, sanitize(props ?? {}));
}

/**
 * Logout / hesap silme sonrası identifier'ı sıfırla.
 */
export async function reset(): Promise<void> {
  const m = await ensureLoaded();
  m?.reset();
}

/**
 * PII filtreleme — propname siyah listesi.
 */
function sanitize(props: Record<string, unknown>): Record<string, unknown> {
  const FORBIDDEN = ['email', 'fullName', 'phone', 'password', 'pan', 'iban', 'cvv', 'cardNumber'];
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props)) {
    if (FORBIDDEN.includes(k)) continue;
    out[k] = v;
  }
  return out;
}
